import React from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { TIntegrationCatalogRow } from "../types";
import TOKENS from "../styles/tokens";

type Props = {
  catalog: TIntegrationCatalogRow[];
  selectedIntegrationType: string | null;
  onSelect: (integrationType: string) => void;
};

const STATUS_LABEL: Record<TIntegrationCatalogRow["status"], string> = {
  ready: "Ready",
  existing_flow: "Use Existing Flow",
  coming_soon: "Request Custom Integration",
};

const IntegrationTypeSelector: React.FC<Props> = ({
  catalog,
  selectedIntegrationType,
  onSelect,
}) => {
  return (
    <ScrollView contentContainerStyle={styles.content}>
      {catalog.map((row) => {
        const isSelected = selectedIntegrationType === row.integration_type;
        const isRequestOnly = row.status === "coming_soon";

        return (
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ selected: isSelected }}
            key={row.integration_type}
            onPress={() => onSelect(row.integration_type)}
            style={({ pressed }) => [
              styles.card,
              isSelected ? styles.cardSelected : null,
              pressed ? styles.cardPressed : null,
              isRequestOnly ? styles.cardRequestOnly : null,
            ]}
          >
            <View style={styles.cardTopRow}>
              <Text style={styles.title}>{row.display_name}</Text>
              <View
                style={[
                  styles.statusPill,
                  row.status === "ready"
                    ? styles.statusReady
                    : row.status === "existing_flow"
                      ? styles.statusExisting
                      : styles.statusComingSoon,
                ]}
              >
                <Text style={styles.statusPillText}>
                  {STATUS_LABEL[row.status]}
                </Text>
              </View>
            </View>
            <Text style={styles.description}>{row.description}</Text>
            {row.required_steps.length > 0 && (
              <Text style={styles.requiredText}>
                Required: {row.required_steps.join(", ")}
              </Text>
            )}
            {row.disabled_reason ? (
              <Text style={styles.disabledReason}>{row.disabled_reason}</Text>
            ) : null}
          </Pressable>
        );
      })}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  content: {
    gap: TOKENS.spacing.md,
    paddingBottom: TOKENS.spacing.sm,
  },
  card: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: 1,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    minHeight: 120,
    justifyContent: "center",
    ...TOKENS.shadow.card,
  },
  cardSelected: {
    borderColor: TOKENS.color.focus,
    backgroundColor: TOKENS.color.infoBg,
  },
  cardPressed: {
    opacity: 0.9,
  },
  cardRequestOnly: {
    borderColor: TOKENS.color.warning,
    backgroundColor: TOKENS.color.warningBg,
  },
  cardTopRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: TOKENS.spacing.sm,
    marginBottom: TOKENS.spacing.xs,
  },
  title: {
    flex: 1,
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.lg,
    lineHeight: TOKENS.lineHeight.lg,
    color: TOKENS.color.textPrimary,
  },
  description: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
    color: TOKENS.color.textSecondary,
    marginBottom: TOKENS.spacing.xs,
  },
  requiredText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.textMuted,
  },
  disabledReason: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.warning,
    marginTop: TOKENS.spacing.xs,
  },
  statusPill: {
    borderRadius: TOKENS.radius.pill,
    paddingHorizontal: TOKENS.spacing.sm,
    paddingVertical: TOKENS.spacing.xxs,
  },
  statusPillText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.xs,
    lineHeight: TOKENS.lineHeight.xs,
    color: TOKENS.color.textPrimary,
  },
  statusReady: {
    backgroundColor: TOKENS.color.successBg,
  },
  statusExisting: {
    backgroundColor: TOKENS.color.infoBg,
  },
  statusComingSoon: {
    backgroundColor: TOKENS.color.warningBg,
    borderWidth: 1,
    borderColor: TOKENS.color.warning,
  },
});

export default IntegrationTypeSelector;
